// src/components/modals/CreateEditBankDialog.tsx
import {
  Box,
  Button,
  Collapsible,
  Drawer,
  Field,
  FieldErrorText,
  FieldLabel,
  FieldRoot,
  Flex,
  HStack,
  IconButton,
  Input,
  Portal,
  SelectContent,
  SelectIndicator,
  SelectItem,
  SelectPositioner,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
  SwitchControl,
  SwitchHiddenInput,
  SwitchRoot,
  SwitchThumb,
  SwitchThumbIndicator,
  Tabs,
  Text,
  createListCollection,
} from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { FieldArray, FormikProvider, getIn, useFormik, type FormikErrors } from 'formik'
import * as Yup from 'yup'
import { HiCheck, HiX } from 'react-icons/hi'
import { LuArrowRight, LuMinus, LuPlus, LuSave, LuTrash2 } from 'react-icons/lu'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import type { Bank, BankService } from '@/common/settings.common'
import { UNION_INVESTMENT_NAME } from '@/common/unionBank.common'
import type {
  BankPartialUpdatePayload,
  BankServicePriceChangeDto,
  CreateBankPayload,
} from '@/api/dto/settings.dto'
import {
  dropPlannedPriceChange,
  getBankById,
  getPriceChangeHistory,
  updateBankService,
} from '@/api/settings.api'
import { DeleteBankDialog } from '@/components/modals/DeleteBankDialog'
import { Tooltip } from '@/components/ui/tooltip'
import { useUserStore } from '@/stores/user.store'
import {
  backendMessageOf,
  buildHistoryRows,
  formatEuro,
  formatIsoDateDE,
  isAlreadyInEffectError,
  parsePriceInput,
  todayInBusinessZone,
  validateEffectiveDate,
  type HistoryRow,
  type IsoDate,
  type PendingValueChange,
  type PlannedValue,
} from '@/helper/valueChange.helper'
import { EffectiveDatePicker } from '@/components/settings/EffectiveDatePicker'
import { ServiceValueSummary } from '@/components/settings/ServiceValueSummary'
import { ValueChangeConfirmDialog } from '@/components/settings/ValueChangeConfirmDialog'
import { DropPlannedChangeDialog } from '@/components/settings/DropPlannedChangeDialog'
import { ValueChangeHistoryTable } from '@/components/settings/ValueChangeHistoryTable'

export interface ServiceRow {
  serviceCode: string
  serviceName: string
  pricePerUnit: string

  /*
   * Date-versioned prices (SIB-941), filled for the Union bank only. `pricePerUnit` is what the
   * user is entering; `activePrice` is the price in effect today and never changes through the
   * form. A change needs `validFrom`; editing a planned change carries its id in
   * `editingChangeId` so the save can replace exactly that plan.
   */
  activePrice?: string
  activeSince?: string | null
  priceVersioned?: boolean
  plannedChanges?: PlannedValue[]
  validFrom?: IsoDate | ''
  editingChangeId?: number | null
}

export interface BankFormData {
  name: string
  street: string
  streetNumber: string
  postal: string
  city: string
  country: string
  autoInvoice: boolean
  invoiceTemplate: string
  parentId: number
  bankServices: ServiceRow[]
  contactName: string
  contactEmail: string
  contactPhone: string
}

export interface CreateEditBankDialogProps {
  isOpen: boolean
  onOpenChange?: (open: boolean) => void
  mode: 'create' | 'edit'
  initialBank?: Partial<Bank>
  serviceTypes: { label: string; value: string }[]
  onSubmit: (data: Partial<CreateBankPayload>) => Promise<'ok' | 'error'>
  onEnsureService?: (input: {
    name: string
    code: string
  }) => Promise<{ name: string; code: string }>
  isProtected?: boolean
  onDeleted?: () => void
  /** Called after the Union prices were saved through their own endpoint (SIB-941). */
  onSaved?: () => void | Promise<void>
  /** Today in the business zone; injectable for tests. */
  today?: IsoDate
}

const UNION_SERVICE_LABEL_BY_CODE: Record<string, string> = {
  TOJ: 'TOJ',
  CONSULTING: 'Beratung',
  TRAINING: 'Schulung',
  PILOT: 'Pilot',
}

const PILOT_FIXED_PRICE = '50'

function unionServiceLabel(code?: string, name?: string) {
  const c = (code ?? '').trim().toUpperCase()
  if (c && UNION_SERVICE_LABEL_BY_CODE[c]) return UNION_SERVICE_LABEL_BY_CODE[c]

  // fallback: backend might deliver English name
  const n = (name ?? '').trim().toLowerCase()
  if (n === 'training on job') return 'TOJ'
  if (n === 'consulting') return 'Beratung'
  if (n === 'training') return 'Schulung'
  if (n === 'pilot') return 'Pilot'

  return name ?? code ?? ''
}

function normalizeBankServices(initialBank?: Partial<Bank>): ServiceRow[] {
  if (!initialBank?.bankServices) return []

  const isUnion = (initialBank?.name ?? '').trim() === UNION_INVESTMENT_NAME

  const seen = new Map<string, ServiceRow>()
  for (const s of initialBank.bankServices as any[]) {
    const code = s.serviceCode
    const name = s.serviceName
    if (!code) continue
    const displayName = isUnion ? unionServiceLabel(code, name) : (name ?? code)

    const activePrice = s.price != null ? String(s.price) : ''
    seen.set(code, {
      serviceCode: code,
      serviceName: displayName,
      pricePerUnit: activePrice,
      ...(isUnion
        ? {
            activePrice,
            activeSince: s.validFrom ?? null,
            priceVersioned: s.priceVersioned ?? code !== 'PILOT',
            plannedChanges: toPlannedValues(s.plannedChanges),
            validFrom: '',
            editingChangeId: null,
          }
        : {}),
    })
  }
  return Array.from(seen.values())
}

function toPlannedValues(planned: BankService['plannedChanges']): PlannedValue[] {
  return (planned ?? []).map(p => ({
    id: p.id,
    value: p.price,
    validFrom: p.validFrom,
    createdBy: p.createdBy,
    createdAt: p.createdAt,
  }))
}

/** `100`, `100.5` and `100,50` compare as the same price; anything unparsable stays as typed. */
function canonicalPrice(v: string | null | undefined) {
  const n = parsePriceInput(v)
  return n === null ? (v ?? '').trim() : String(n)
}

/** Whether a Union row's input differs from what is in effect, or edits a planned change. */
function unionRowChanged(row: ServiceRow): boolean {
  if (row.priceVersioned === false) return false
  if (row.editingChangeId != null) {
    const planned = row.plannedChanges?.find(p => p.id === row.editingChangeId)
    if (!planned) return true
    return (
      canonicalPrice(row.pricePerUnit) !== String(planned.value) ||
      (row.validFrom ?? '') !== planned.validFrom
    )
  }
  return canonicalPrice(row.pricePerUnit) !== canonicalPrice(row.activePrice ?? '')
}

/** A price that exists already needs a date; the very first price takes effect at once. */
function unionRowNeedsDate(row: ServiceRow): boolean {
  return row.priceVersioned !== false && (row.activePrice ?? '') !== ''
}

function toPriceHistoryRows(changes: BankServicePriceChangeDto[]): HistoryRow[] {
  return buildHistoryRows(
    changes.map(c => ({
      id: c.id,
      serviceCode: c.serviceCode,
      serviceLabel: unionServiceLabel(c.serviceCode, c.serviceName),
      value: c.price,
      validFrom: c.validFrom,
      state: c.state,
      createdBy: c.createdBy,
      createdAt: c.createdAt,
      discardedBy: c.discardedBy,
      discardedAt: c.discardedAt,
    }))
  )
}

const UNION_CONTACT_NAME = 'BSS-SCP / SIB-Koordination'
const UNION_CONTACT_EMAIL = 'SIB@union-investment.de'

const unionFixedServices = [
  { code: 'TOJ', label: 'TOJ', fixedPrice: null },
  { code: 'CONSULTING', label: 'Beratung', fixedPrice: null },
  { code: 'TRAINING', label: 'Schulung', fixedPrice: null },
  { code: 'PILOT', label: 'Pilot', fixedPrice: PILOT_FIXED_PRICE },
]

const baseSchema = Yup.object({
  name: Yup.string().required('Dieses Feld ist erforderlich.'),
  street: Yup.string().required('Dieses Feld ist erforderlich.'),
  streetNumber: Yup.string().required('Dieses Feld ist erforderlich.'),
  postal: Yup.string()
    .matches(/^\d+$/, 'Nur Zahlen erlaubt.')
    .required('Dieses Feld ist erforderlich.'),
  city: Yup.string().required('Dieses Feld ist erforderlich.'),
  country: Yup.string().required('Bitte auswählen.'),
  contactName: Yup.string().required('Dieses Feld ist erforderlich.'),
  contactEmail: Yup.string()
    .email('Ungültige E-Mail-Adresse')
    .required('Dieses Feld ist erforderlich.'),
  contactPhone: Yup.string().optional(),
  autoInvoice: Yup.boolean(),
  bankServices: Yup.array()
    .of(
      Yup.object({
        serviceCode: Yup.string().required('Bitte Leistung auswählen'),
        pricePerUnit: Yup.string()
          .required('Preis ist erforderlich')
          .matches(/^\d+(\.\d{1,2})?$/, 'Nur gültige Zahlen (z.B. 10 oder 10.50)'),
        // .test('gt0', 'Preis muss größer als 0 sein', v => parseFloat(v || '0') > 0),
      })
    )
    .min(0, 'Mindestens eine Leistung erforderlich')
    .test('unique-serviceCode', 'Leistungen dürfen nicht doppelt sein', rows => {
      const vals = (rows ?? []).map(r => (r?.serviceCode ?? '').trim()).filter(Boolean)
      return new Set(vals).size === vals.length
    }),
})

const protectedUnionSchema = Yup.object({
  // union: only phone is editable, so keep these optional to avoid blocking Step 1
  name: Yup.string().optional(),
  street: Yup.string().optional(),
  streetNumber: Yup.string().optional(),
  postal: Yup.string().optional(),
  city: Yup.string().optional(),
  country: Yup.string().optional(),
  contactName: Yup.string().optional(),
  contactEmail: Yup.string().optional(),
  contactPhone: Yup.string().optional(),
  autoInvoice: Yup.boolean(),
  bankServices: Yup.array()
    .of(
      Yup.object({
        serviceCode: Yup.string().required('Bitte Leistung auswählen'),
        pricePerUnit: Yup.string()
          .required('Preis ist erforderlich')
          .matches(/^\d+([.,]\d{1,2})?$/, 'Nur gültige Zahlen (z.B. 10 oder 10.50)'),
        // .test('gt0', 'Preis muss größer als 0 sein', v => parseFloat(v || '0') > 0),
        validFrom: Yup.string().test(
          'effective-date',
          'Bitte wählen Sie, ab wann die Änderung gilt.',
          function (value) {
            const row = this.parent as ServiceRow
            if (!unionRowChanged(row) || !unionRowNeedsDate(row)) return true
            return validateEffectiveDate(value, todayInBusinessZone()) === null
          }
        ),
      })
    )
    .min(0, 'Mindestens eine Leistung erforderlich'),
})

type TabKey = 'MASTER' | 'SERVICES'

export function CreateEditBankDialog({
  isOpen,
  onOpenChange,
  mode,
  initialBank,
  serviceTypes,
  onSubmit,
  onEnsureService,
  isProtected = false,
  onDeleted,
  onSaved,
  today,
}: CreateEditBankDialogProps) {
  const [submitting, setSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState<TabKey>('MASTER')
  const [deleteOpen, setDeleteOpen] = useState(false)

  // Union prices with an effective date (SIB-941)
  const todayIso = today ?? todayInBusinessZone()
  const currentUser = useUserStore(s => s.user)
  const [pendingPriceChanges, setPendingPriceChanges] = useState<PendingValueChange[] | null>(null)
  const [priceHistory, setPriceHistory] = useState<HistoryRow[]>([])
  const [priceHistoryAvailable, setPriceHistoryAvailable] = useState(true)
  const [priceHistoryOpen, setPriceHistoryOpen] = useState(false)
  const [dropTarget, setDropTarget] = useState<{ index: number; planned: PlannedValue } | null>(
    null
  )
  const [dropping, setDropping] = useState(false)

  const isEdit = mode === 'edit'
  const bankId = initialBank?.id
  function normStr(v: any) {
    return (v ?? '').toString().trim()
  }

  function normBool(v: any) {
    return !!v
  }

  function resetAll() {
    formik.resetForm({ values: initialFormValues })
    setActiveTab('MASTER')
    setDeleteOpen(false)
    setSubmitting(false)
  }

  function normServices(rows: ServiceRow[]) {
    return (
      (rows ?? [])
        .map(r => ({
          serviceCode: normStr(r.serviceCode),
          serviceName: normStr(r.serviceName), // optional, keep if you care
          pricePerUnit: normStr(r.pricePerUnit),
          validFrom: normStr(r.validFrom),
          editingChangeId: r.editingChangeId ?? null,
        }))
        // ignore empty rows
        .filter(r => r.serviceCode || r.serviceName || r.pricePerUnit)
        // stable order (avoid “changed” just because order differs)
        .sort(
          (a, b) =>
            a.serviceCode.localeCompare(b.serviceCode) ||
            a.serviceName.localeCompare(b.serviceName)
        )
    )
  }

  function stableStringify(obj: any) {
    // good enough for these simple objects/arrays
    return JSON.stringify(obj)
  }

  function normalizeForCompare(
    v: BankFormData,
    opts: { protectedUnion: boolean; isProtected: boolean }
  ) {
    const base = {
      // master
      name: normStr(v.name),
      street: normStr(v.street),
      streetNumber: normStr(v.streetNumber),
      postal: normStr(v.postal),
      city: normStr(v.city),
      country: normStr(v.country),
      autoInvoice: normBool(v.autoInvoice),
      invoiceTemplate: normStr(v.invoiceTemplate),
      parentId: Number(v.parentId ?? 0),

      // contact
      contactName: normStr(v.contactName),
      contactEmail: normStr(v.contactEmail),
      contactPhone: normStr(v.contactPhone),

      // services
      bankServices: normServices(v.bankServices),
    }

    // Union protected: only phone + prices/services should count as “change”
    if (opts.protectedUnion) {
      return {
        contactPhone: base.contactPhone,
        bankServices: base.bankServices.map(s => ({
          serviceCode: s.serviceCode,
          pricePerUnit: canonicalPrice(s.pricePerUnit),
          validFrom: s.validFrom,
          editingChangeId: s.editingChangeId,
        })),
      }
    }

    // Other protected: in your code you only submit contactDetails + bankServices
    if (opts.isProtected) {
      return {
        contactName: base.contactName,
        contactEmail: base.contactEmail,
        contactPhone: base.contactPhone,
        bankServices: base.bankServices.map(s => ({
          serviceCode: s.serviceCode,
          pricePerUnit: s.pricePerUnit,
        })),
      }
    }

    return base
  }
  const initialFormValues = useMemo<BankFormData>(
    () => ({
      name: initialBank?.name ?? '',
      street: initialBank?.street ?? '',
      streetNumber: initialBank?.streetNumber ?? '',
      postal: initialBank?.postal ?? '',
      city: initialBank?.city ?? '',
      country: initialBank?.country ?? '',
      autoInvoice: initialBank?.autoInvoice ?? false,
      invoiceTemplate: initialBank?.invoiceTemplate ?? '',
      parentId: initialBank?.parentId ?? 0,
      bankServices: normalizeBankServices(initialBank),
      contactName: initialBank?.contactDetails?.contactName ?? '',
      contactEmail: initialBank?.contactDetails?.contactEmail ?? '',
      contactPhone: initialBank?.contactDetails?.contactPhone ?? '',
    }),
    [initialBank]
  )

  const formik = useFormik<BankFormData>({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema:
      isProtected && (initialBank?.name ?? '').trim() === UNION_INVESTMENT_NAME
        ? protectedUnionSchema
        : baseSchema,
    validateOnMount: true,
    onSubmit: async values => {
      setSubmitting(true)
      try {
        const unionSelected = values.name.trim() === UNION_INVESTMENT_NAME
        const protectedUnion = unionSelected && isProtected && mode === 'edit'

        const fixedContactName = protectedUnion ? UNION_CONTACT_NAME : values.contactName
        const fixedContactEmail = protectedUnion ? UNION_CONTACT_EMAIL : values.contactEmail

        const contactDetails = {
          contactName: fixedContactName?.trim() || null,
          contactEmail: fixedContactEmail?.trim() || null,
          contactPhone: values.contactPhone?.trim() || null,
        }

        let payload: CreateBankPayload | BankPartialUpdatePayload

        const mappedServices = values.bankServices.map(s => ({
          serviceCode: s.serviceCode,
          serviceName: s.serviceName || s.serviceCode,
          price: parseFloat(s.pricePerUnit),
        }))

        if (protectedUnion) {
          // Union (SIB-941): prices are versioned and go through their own endpoint, one service
          // per call, each with its own effective date. The bank-level update carries the contact
          // details only - it must never resend prices, because that path writes them without a
          // date and drops every service it does not mention.
          const priceChanges = collectUnionPriceChanges(values.bankServices)
          if (priceChanges.length > 0) {
            setPendingPriceChanges(priceChanges)
            return
          }
          await performUnionSave(contactDetails, [])
          return
        }

        if (!isEdit) {
          payload = {
            name: values.name,
            street: values.street,
            streetNumber: values.streetNumber,
            postal: values.postal,
            city: values.city,
            country: values.country,
            autoInvoice: values.autoInvoice,
            invoiceTemplate: values.invoiceTemplate,
            parentId: values.parentId,
            contactDetails,
            bankServices: mappedServices,
          }
        } else {
          if (protectedUnion) {
            payload = {
              contactDetails,
              bankServices: mappedServices,
            }
          } else if (isProtected) {
            // protected non-union (if ever used): keep conservative
            payload = {
              contactDetails,
              bankServices: mappedServices,
            }
          } else {
            payload = {
              name: values.name,
              street: values.street,
              streetNumber: values.streetNumber,
              postal: values.postal,
              city: values.city,
              country: values.country,
              autoInvoice: values.autoInvoice,
              invoiceTemplate: values.invoiceTemplate,
              parentId: values.parentId,
              contactDetails,
              bankServices: mappedServices,
            }
          }
        }

        const result = await onSubmit(payload as any)

        if (result === 'ok') {
          toast.success(
            mode === 'create' ? 'Auftraggeber erstellt' : 'Auftraggeber aktualisiert',
            'Ihre Änderungen wurden erfolgreich gespeichert.'
          )
          onOpenChange?.(false)
          setActiveTab('MASTER')
        } else {
          toast.error(
            'Auftraggeber konnte nicht gespeichert werden',
            'Bitte führen Sie den Vorgang erneut aus.'
          )
        }
      } catch (e: any) {
        // keep your INVALID_PAYLOAD mapper (still works)
        if (e?.code === 'INVALID_PAYLOAD' && e?.errors) {
          const fieldMap: Record<string, keyof BankFormData> = {
            'contactDetails.contactName': 'contactName',
            'contactDetails.contactEmail': 'contactEmail',
            'contactDetails.contactPhone': 'contactPhone',
          }
          let applied = false
          for (const [backendPath, messages] of Object.entries(
            e.errors as Record<string, string[]>
          )) {
            const formField = fieldMap[backendPath]
            const msg = Array.isArray(messages) ? messages[0] : undefined
            if (formField && msg) {
              formik.setFieldTouched(formField, true, false)
              formik.setFieldError(formField, msg)
              applied = true
            }
          }
          if (applied) return
        }
        toast.error(
          'Auftraggeber konnte nicht gespeichert werden',
          'Bitte führen Sie den Vorgang erneut aus.'
        )
      } finally {
        setSubmitting(false)
      }
    },
  })

  const isUnionInvestment = formik.values.name.trim() === UNION_INVESTMENT_NAME
  const isProtectedUnion = isUnionInvestment && isProtected && mode === 'edit'

  /* ----------------------- Union prices with a date (SIB-941) ----------------------- */

  function collectUnionPriceChanges(rows: ServiceRow[]): PendingValueChange[] {
    return rows.filter(unionRowChanged).map(row => {
      const active = row.activePrice ? parsePriceInput(row.activePrice) : null
      const replaces =
        row.editingChangeId != null
          ? (row.plannedChanges?.find(p => p.id === row.editingChangeId) ?? null)
          : null
      return {
        serviceCode: row.serviceCode,
        serviceLabel: row.serviceName || row.serviceCode,
        activeValue: active,
        newValue: parsePriceInput(row.pricePerUnit) ?? 0,
        validFrom: unionRowNeedsDate(row) ? row.validFrom || null : null,
        replaces,
      }
    })
  }

  function phoneChanged() {
    return normStr(formik.values.contactPhone) !== normStr(initialFormValues.contactPhone)
  }

  async function reloadAfterTakingEffect() {
    toast.info(
      'Die Änderung ist inzwischen wirksam',
      'Das Datum dieser Änderung ist erreicht; sie kann nicht mehr bearbeitet oder verworfen werden. Die Ansicht wurde aktualisiert.'
    )
    await refreshUnionRows()
  }

  /**
   * Re-reads the Union bank and merges the fresh active price and planned changes into the rows
   * without discarding what the user typed elsewhere in the form.
   */
  async function refreshUnionRows() {
    if (bankId == null) return
    try {
      const fresh = await getBankById(bankId)
      const freshRows = normalizeBankServices(fresh)
      const merged = formik.values.bankServices.map(row => {
        const f = freshRows.find(x => x.serviceCode === row.serviceCode)
        if (!f) return row
        const stillPlanned =
          row.editingChangeId != null && f.plannedChanges?.some(p => p.id === row.editingChangeId)
        const editingLost = row.editingChangeId != null && !stillPlanned
        return {
          ...row,
          activePrice: f.activePrice,
          activeSince: f.activeSince,
          priceVersioned: f.priceVersioned,
          plannedChanges: f.plannedChanges,
          ...(editingLost
            ? { pricePerUnit: f.activePrice ?? '', validFrom: '' as const, editingChangeId: null }
            : {}),
        }
      })
      formik.setFieldValue('bankServices', merged, true)
      await loadPriceHistory()
      await onSaved?.()
    } catch {
      toast.error(
        'Die Preise konnten nicht neu geladen werden. Bitte öffnen Sie den Dialog erneut.'
      )
    }
  }

  async function loadPriceHistory() {
    if (bankId == null) return
    try {
      const h = await getPriceChangeHistory(bankId)
      setPriceHistory(toPriceHistoryRows(h.changes ?? []))
      setPriceHistoryAvailable(true)
    } catch {
      setPriceHistory([])
      setPriceHistoryAvailable(false)
    }
  }

  async function performUnionSave(
    contactDetails: {
      contactName: string | null
      contactEmail: string | null
      contactPhone: string | null
    },
    priceChanges: PendingValueChange[]
  ) {
    if (bankId == null) return
    setSubmitting(true)
    try {
      if (phoneChanged()) {
        const result = await onSubmit({ contactDetails } as Partial<CreateBankPayload>)
        if (result !== 'ok') {
          toast.error(
            'Auftraggeber konnte nicht gespeichert werden',
            'Bitte führen Sie den Vorgang erneut aus.'
          )
          return
        }
      }

      const saved: string[] = []
      const leftovers: string[] = []
      for (const change of priceChanges) {
        const row = formik.values.bankServices.find(r => r.serviceCode === change.serviceCode)
        await updateBankService(bankId, {
          serviceCode: change.serviceCode,
          serviceName: row?.serviceName || change.serviceCode,
          price: change.newValue,
          ...(change.validFrom ? { validFrom: change.validFrom } : {}),
        })
        saved.push(
          change.validFrom
            ? `${change.serviceLabel} gilt ab ${formatIsoDateDE(change.validFrom)}`
            : `${change.serviceLabel} gilt ab sofort`
        )
        // A plan moved to another date: the backend only replaces a plan for the same date, so
        // the old one is dropped explicitly, after the new one is safely written.
        if (change.replaces && change.replaces.validFrom !== change.validFrom) {
          try {
            await dropPlannedPriceChange(bankId, change.replaces.id)
          } catch {
            leftovers.push(
              `${change.serviceLabel} ab ${formatIsoDateDE(change.replaces.validFrom)}`
            )
          }
        }
      }

      setPendingPriceChanges(null)
      toast.success(
        'Änderung gespeichert',
        saved.length > 0
          ? `${saved.join(', ')}.`
          : 'Ihre Änderungen wurden erfolgreich gespeichert.'
      )
      if (leftovers.length > 0) {
        toast.warning(
          'Bisherige Planung noch vorhanden',
          `Die bisher geplante Änderung (${leftovers.join(', ')}) konnte nicht verworfen werden. Bitte verwerfen Sie sie manuell.`
        )
      }
      await onSaved?.()
      onOpenChange?.(false)
      setActiveTab('MASTER')
    } catch (err) {
      setPendingPriceChanges(null)
      if (isAlreadyInEffectError(err)) {
        await reloadAfterTakingEffect()
      } else {
        toast.error(
          'Preis konnte nicht gespeichert werden',
          backendMessageOf(err) ?? 'Bitte führen Sie den Vorgang erneut aus.'
        )
      }
    } finally {
      setSubmitting(false)
    }
  }

  async function confirmUnionSave() {
    if (!pendingPriceChanges) return
    await performUnionSave(
      {
        contactName: UNION_CONTACT_NAME,
        contactEmail: UNION_CONTACT_EMAIL,
        contactPhone: formik.values.contactPhone?.trim() || null,
      },
      pendingPriceChanges
    )
  }

  function startEditingPlanned(index: number, planned: PlannedValue) {
    formik.setFieldValue(`bankServices.${index}.pricePerUnit`, String(planned.value))
    formik.setFieldValue(`bankServices.${index}.validFrom`, planned.validFrom)
    formik.setFieldValue(`bankServices.${index}.editingChangeId`, planned.id)
  }

  function stopEditingPlanned(index: number) {
    const row = formik.values.bankServices[index]
    formik.setFieldValue(`bankServices.${index}.pricePerUnit`, row?.activePrice ?? '')
    formik.setFieldValue(`bankServices.${index}.validFrom`, '')
    formik.setFieldValue(`bankServices.${index}.editingChangeId`, null)
  }

  async function doDropPlanned() {
    if (!dropTarget || bankId == null || dropping) return
    setDropping(true)
    const row = formik.values.bankServices[dropTarget.index]
    try {
      await dropPlannedPriceChange(bankId, dropTarget.planned.id)
      toast.success(
        'Geplante Änderung verworfen',
        `${row?.serviceName ?? ''}: Der aktuelle Preis gilt weiterhin.`
      )
      setDropTarget(null)
      await refreshUnionRows()
    } catch (err) {
      setDropTarget(null)
      if (isAlreadyInEffectError(err)) {
        await reloadAfterTakingEffect()
      } else {
        toast.error(
          'Geplante Änderung konnte nicht verworfen werden',
          backendMessageOf(err) ?? 'Bitte versuchen Sie es erneut.'
        )
      }
    } finally {
      setDropping(false)
    }
  }
  const baseline = useMemo(() => {
    return normalizeForCompare(initialFormValues, {
      protectedUnion: isProtectedUnion,
      isProtected,
    })
  }, [initialFormValues, isProtectedUnion, isProtected])

  const current = useMemo(() => {
    return normalizeForCompare(formik.values, {
      protectedUnion: isProtectedUnion,
      isProtected,
    })
  }, [formik.values, isProtectedUnion, isProtected])

  const hasRealChanges = useMemo(() => {
    return stableStringify(baseline) !== stableStringify(current)
  }, [baseline, current])

  // Ensure fixed union contact values
  useEffect(() => {
    if (!isProtectedUnion) return
    if (formik.values.contactName !== UNION_CONTACT_NAME) {
      formik.setFieldValue('contactName', UNION_CONTACT_NAME, false)
    }
    if (formik.values.contactEmail !== UNION_CONTACT_EMAIL) {
      formik.setFieldValue('contactEmail', UNION_CONTACT_EMAIL, false)
    }
    // seed fixed services if missing
    const next = unionFixedServices.map(u => {
      const found = formik.values.bankServices.find(s => s.serviceCode === u.code)
      return (
        found ?? {
          serviceCode: u.code,
          serviceName: u.label,
          pricePerUnit: u.fixedPrice ?? '',
          activePrice: u.fixedPrice ?? '',
          activeSince: null,
          priceVersioned: u.fixedPrice === null,
          plannedChanges: [],
          validFrom: '' as const,
          editingChangeId: null,
        }
      )
    })
    // only set when materially different (avoid loops)
    const codes = formik.values.bankServices.map(s => s.serviceCode).join('|')
    const nextCodes = next.map(s => s.serviceCode).join('|')
    if (codes !== nextCodes || formik.values.bankServices.length !== next.length) {
      formik.setFieldValue('bankServices', next, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProtectedUnion])

  useEffect(() => {
    if (!isOpen) {
      resetAll()
      setPendingPriceChanges(null)
      setDropTarget(null)
      setPriceHistory([])
      setPriceHistoryOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (isOpen && isProtectedUnion) loadPriceHistory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isProtectedUnion, bankId])

  const title = mode === 'create' ? 'Auftraggeber anlegen' : 'Auftraggeber bearbeiten'

  const countryCollection = useMemo(
    () =>
      createListCollection({
        items: [
          { label: 'Deutschland', value: 'de' },
          { label: 'Österreich', value: 'at' },
          { label: 'Schweiz', value: 'ch' },
        ],
      }),
    []
  )

  const step1Fields: (keyof BankFormData)[] = [
    'name',
    'street',
    'streetNumber',
    'postal',
    'city',
    'country',
    'contactName',
    'contactEmail',
  ]

  function isStep1Valid() {
    // union protected: allow stepping even though fields are disabled
    if (isProtectedUnion) return true

    for (const f of step1Fields) {
      const err = getIn(formik.errors, f)
      const val = (formik.values as any)[f]
      if (err) return false
      if (typeof val === 'string' && !val.trim()) return false
    }
    return true
  }

  const servicesTabDisabled = !isStep1Valid()

  async function goNext() {
    if (isProtectedUnion) {
      setActiveTab('SERVICES')
      return
    }

    step1Fields.forEach(f => formik.setFieldTouched(f, true, false))
    await formik.validateForm()
    if (isStep1Valid()) setActiveTab('SERVICES')
  }

  const hasAtLeastOneCompleteService = formik.values.bankServices.some(
    s => s.serviceCode?.trim() && s.pricePerUnit?.trim()
  )

  const baseCanSave = formik.isValid && hasAtLeastOneCompleteService && !submitting
  const canSave = isEdit ? baseCanSave && hasRealChanges : baseCanSave

  /** The Union rows in display order, each with its index in the form array. */
  const unionRows = useMemo(
    () =>
      formik.values.bankServices
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => !!row.serviceCode),
    [formik.values.bankServices]
  )

  const disableMasterFields = isProtected || isProtectedUnion
  const disableMasterContact = isProtectedUnion // union: contactName/email fixed & disabled
  const allowPhoneEdit = true // always editable per mock (incl union)

  const normalizeCode = (v: string) => v.trim()

  function getAvailableServiceTypes(currentIndex: number) {
    const selectedCodes = (formik.values.bankServices || [])
      .map((s, i) => (i === currentIndex ? null : normalizeCode(s.serviceCode)))
      .filter(Boolean) as string[]

    const base = isProtectedUnion
      ? unionFixedServices.map(x => ({ label: x.label, value: x.code }))
      : serviceTypes
    const nonUnionBase = isProtectedUnion ? base : base.filter(st => st.value !== 'TOJ')

    return nonUnionBase.filter(st => !selectedCodes.includes(st.value))
  }

  function toServiceCode(name: string) {
    return name
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
  }

  async function ensureServiceFromName(index: number) {
    if (!onEnsureService) return
    const row = formik.values.bankServices[index]
    const name = row?.serviceName?.trim()
    if (!name) return

    const match = serviceTypes.find(s => s.label.trim().toLowerCase() === name.toLowerCase())
    if (match) {
      formik.setFieldValue(`bankServices.${index}.serviceCode`, match.value)
      formik.setFieldValue(`bankServices.${index}.serviceName`, match.label)
      return
    }

    const code = toServiceCode(name)
    formik.setFieldValue(`bankServices.${index}.serviceCode`, code)
    formik.setFieldValue(`bankServices.${index}.serviceName`, name)

    try {
      await onEnsureService({ name, code })
    } catch (e) {
      // optional: show toast or ignore
      console.error('Failed to ensure service', e)
    }
  }

  async function handleDeleteService(index: number) {
    // for this redesign: deletion is UI-only here; backend deletion remains handled by update payload
    formik.setFieldValue(
      'bankServices',
      formik.values.bankServices.filter((_, i) => i !== index)
    )
  }

  const showDeleteButton = isEdit && !isProtectedUnion && !isProtected && !!bankId

  return (
    <Drawer.Root
      open={isOpen}
      size="xl"
      onOpenChange={e => {
        if (!e.open) resetAll()
        onOpenChange?.(e.open)
      }}
      // The "Gültig ab" calendar is a portaled popover, so a click on one of its months lands
      // outside the drawer in the DOM. It must not dismiss the drawer and reset the form.
      onInteractOutside={e => {
        const target = e.detail.originalEvent?.target as Element | null
        if (target?.closest?.('[data-scope="popover"]')) e.preventDefault()
      }}
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content data-testid="create-edit-bank-dialog">
            {/* HEADER */}
            <Drawer.Header>
              <HStack gap={4}>
                <Drawer.Title>{title}</Drawer.Title>
                {showDeleteButton && (
                  <Button variant="subtle" colorPalette="red" onClick={() => setDeleteOpen(true)}>
                    <LuTrash2 />
                    Auftraggeber löschen
                  </Button>
                )}
              </HStack>
            </Drawer.Header>

            <Drawer.Body>
              <Flex direction="column" gap={8}>
                {isProtectedUnion && (
                  <Text>
                    Union Investment ist als Auftraggeber fest hinterlegt und kann nicht gelöscht
                    werden. Bitte geben Sie hier ausschließlich die vereinbarten Preise Ihrer
                    Leistungen an.
                  </Text>
                )}

                <Tabs.Root
                  value={activeTab}
                  onValueChange={event => setActiveTab(event.value as TabKey)}
                  variant="subtle"
                  colorPalette="gray"
                >
                  <Tabs.List>
                    <Tabs.Trigger value="MASTER">Stammdaten</Tabs.Trigger>

                    <Tooltip
                      disabled={!servicesTabDisabled || isProtectedUnion}
                      content="Tab ist deaktiviert bis alle Pflichtfelder der Stammdaten ausgefüllt sind."
                    >
                      <Tabs.Trigger value="SERVICES" disabled={servicesTabDisabled}>
                        Leistungen &amp; Preise
                      </Tabs.Trigger>
                    </Tooltip>
                  </Tabs.List>
                </Tabs.Root>

                <Box>
                  <FormikProvider value={formik}>
                    {activeTab === 'MASTER' ? (
                      <Flex direction="column" gap={8}>
                        <Text>Stammdaten</Text>

                        <Box
                          display="grid"
                          columnGap={4}
                          rowGap={6}
                          gridTemplateColumns={{
                            base: '1fr',
                            md: 'repeat(12, minmax(0, 1fr))',
                          }}
                        >
                          <FieldRoot
                            invalid={!!formik.errors.name && !!formik.touched.name}
                            gridColumn={{ base: 'auto', md: '1 / -1' }}
                          >
                            <FieldLabel>
                              Name des Auftraggebers <Field.RequiredIndicator />
                            </FieldLabel>
                            <Input
                              name="name"
                              value={formik.values.name}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              placeholder="Musterbank"
                              disabled={disableMasterFields}
                            />
                            <FieldErrorText>
                              {formik.touched.name && formik.errors.name}
                            </FieldErrorText>
                          </FieldRoot>

                          <FieldRoot
                            invalid={!!formik.errors.street && !!formik.touched.street}
                            gridColumn={{ base: 'auto', md: 'span 8' }}
                          >
                            <FieldLabel>
                              Straße <Field.RequiredIndicator />
                            </FieldLabel>
                            <Input
                              name="street"
                              value={formik.values.street}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              placeholder="Musterstraße"
                              disabled={disableMasterFields}
                            />
                            <FieldErrorText>
                              {formik.touched.street && formik.errors.street}
                            </FieldErrorText>
                          </FieldRoot>

                          <FieldRoot
                            invalid={!!formik.errors.streetNumber && !!formik.touched.streetNumber}
                            gridColumn={{ base: 'auto', md: 'span 4' }}
                          >
                            <FieldLabel>
                              Hausnummer <Field.RequiredIndicator />
                            </FieldLabel>
                            <Input
                              name="streetNumber"
                              value={formik.values.streetNumber}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              placeholder="Nr."
                              disabled={disableMasterFields}
                            />
                            <FieldErrorText>
                              {formik.touched.streetNumber && formik.errors.streetNumber}
                            </FieldErrorText>
                          </FieldRoot>

                          <FieldRoot
                            invalid={!!formik.errors.postal && !!formik.touched.postal}
                            gridColumn={{ base: 'auto', md: 'span 4' }}
                          >
                            <FieldLabel>
                              Postleitzahl <Field.RequiredIndicator />
                            </FieldLabel>
                            <Input
                              name="postal"
                              value={formik.values.postal}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              placeholder="12345"
                              disabled={disableMasterFields}
                            />
                            <FieldErrorText>
                              {formik.touched.postal && formik.errors.postal}
                            </FieldErrorText>
                          </FieldRoot>

                          <FieldRoot
                            invalid={!!formik.errors.city && !!formik.touched.city}
                            gridColumn={{ base: 'auto', md: 'span 8' }}
                          >
                            <FieldLabel>
                              Stadt <Field.RequiredIndicator />
                            </FieldLabel>
                            <Input
                              name="city"
                              value={formik.values.city}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              placeholder="Musterstadt"
                              disabled={disableMasterFields}
                            />
                            <FieldErrorText>
                              {formik.touched.city && formik.errors.city}
                            </FieldErrorText>
                          </FieldRoot>

                          <FieldRoot
                            invalid={!!formik.errors.country && !!formik.touched.country}
                            gridColumn={{ base: 'auto', md: 'span 6' }}
                          >
                            <FieldLabel>
                              Land <Field.RequiredIndicator />
                            </FieldLabel>

                            <SelectRoot
                              value={[formik.values.country]}
                              onValueChange={v => formik.setFieldValue('country', v.value[0])}
                              collection={countryCollection}
                              disabled={disableMasterFields}
                            >
                              <SelectTrigger>
                                <SelectValueText placeholder="Bitte auswählen" />
                                <SelectIndicator />
                              </SelectTrigger>

                              <SelectPositioner>
                                <SelectContent>
                                  {countryCollection.items.map(it => (
                                    <SelectItem key={it.value} item={it.value}>
                                      {it.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </SelectPositioner>
                            </SelectRoot>

                            <FieldErrorText>
                              {formik.touched.country && (formik.errors.country as any)}
                            </FieldErrorText>
                          </FieldRoot>

                          <FieldRoot
                            invalid={!!formik.errors.contactName && !!formik.touched.contactName}
                            gridColumn={{ base: 'auto', md: 'span 6' }}
                          >
                            <FieldLabel>
                              Ansprechpartner <Field.RequiredIndicator />
                            </FieldLabel>
                            <Input
                              name="contactName"
                              value={
                                isProtectedUnion ? UNION_CONTACT_NAME : formik.values.contactName
                              }
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              placeholder="Vor- und Nachname"
                              disabled={disableMasterContact}
                            />
                            <FieldErrorText>
                              {formik.touched.contactName && (formik.errors.contactName as any)}
                            </FieldErrorText>
                          </FieldRoot>

                          <FieldRoot
                            invalid={!!formik.errors.contactEmail && !!formik.touched.contactEmail}
                            gridColumn={{ base: 'auto', md: 'span 6' }}
                          >
                            <FieldLabel>
                              E-Mail Ansprechpartner <Field.RequiredIndicator />
                            </FieldLabel>
                            <Input
                              name="contactEmail"
                              value={
                                isProtectedUnion ? UNION_CONTACT_EMAIL : formik.values.contactEmail
                              }
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              placeholder="muster@mail.de"
                              disabled={disableMasterContact}
                            />
                            <FieldErrorText>
                              {formik.touched.contactEmail && (formik.errors.contactEmail as any)}
                            </FieldErrorText>
                          </FieldRoot>

                          <FieldRoot
                            invalid={!!formik.errors.contactPhone && !!formik.touched.contactPhone}
                            gridColumn={{ base: 'auto', md: 'span 6' }}
                          >
                            <FieldLabel>
                              Telefonnummer Ansprechpartner <Text as="span">optional</Text>
                            </FieldLabel>
                            <Input
                              name="contactPhone"
                              value={formik.values.contactPhone}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              placeholder="+49 123456789"
                              disabled={!allowPhoneEdit ? true : false}
                            />
                            <FieldErrorText>
                              {formik.touched.contactPhone && (formik.errors.contactPhone as any)}
                            </FieldErrorText>
                          </FieldRoot>

                          <HStack gap={3} gridColumn={{ base: 'auto', md: '1 / -1' }}>
                            <SwitchRoot
                              colorPalette="green"
                              checked={formik.values.autoInvoice}
                              disabled={disableMasterFields}
                              style={{ touchAction: 'none' }}
                              onCheckedChange={(detail: { checked: boolean }) => {
                                formik.setFieldValue('autoInvoice', detail.checked)
                                formik.setFieldTouched('autoInvoice', true)
                              }}
                            >
                              <SwitchHiddenInput />
                              <SwitchControl>
                                <SwitchThumb>
                                  <SwitchThumbIndicator fallback={<HiX color="black" />}>
                                    <HiCheck />
                                  </SwitchThumbIndicator>
                                </SwitchThumb>
                              </SwitchControl>
                            </SwitchRoot>
                            <Text>Automatischer Versand der Rechnungen</Text>
                          </HStack>
                        </Box>
                      </Flex>
                    ) : isProtectedUnion ? (
                      <Flex direction="column" gap={8} data-testid="union-price-settings">
                        <Box>
                          <Text>Leistungen &amp; Preise</Text>
                          <Text>
                            Ein neuer Preis gilt ab dem gewählten Datum. Bis dahin bleibt der
                            aktuelle Preis bestehen.
                          </Text>
                        </Box>

                        <Flex direction="column" gap={6}>
                          {unionRows.map(({ row, index }) => {
                            const error = formik.errors.bankServices?.[index] as
                              | FormikErrors<ServiceRow>
                              | undefined
                            const touched = formik.touched.bankServices?.[index] as
                              | FormikErrors<ServiceRow>
                              | undefined
                            const label = row.serviceName || row.serviceCode
                            const fixed = row.priceVersioned === false
                            const changed = unionRowChanged(row)
                            const needsDate = unionRowNeedsDate(row)
                            const editing =
                              row.editingChangeId != null
                                ? (row.plannedChanges?.find(p => p.id === row.editingChangeId) ??
                                  null)
                                : null
                            const activeValue = row.activePrice
                              ? parsePriceInput(row.activePrice)
                              : null
                            const priceLabel = editing
                              ? `Geplanter Preis ab ${formatIsoDateDE(editing.validFrom)}`
                              : activeValue === null
                                ? 'Preis pro Einheit (brutto)'
                                : 'Neuer Preis pro Einheit (brutto)'

                            return (
                              <Box
                                key={row.serviceCode}
                                display="grid"
                                columnGap={4}
                                rowGap={4}
                                alignItems="flex-start"
                                gridTemplateColumns={{
                                  base: '1fr',
                                  md: 'repeat(12, minmax(0, 1fr))',
                                }}
                                data-testid={`union-service-${row.serviceCode}`}
                              >
                                <Flex
                                  direction="column"
                                  gap={2}
                                  gridColumn={{ base: 'auto', md: '1 / -1' }}
                                >
                                  <Text>{label}</Text>
                                  <ServiceValueSummary
                                    kind="price"
                                    serviceLabel={label}
                                    activeValue={activeValue}
                                    activeSince={row.activeSince}
                                    planned={fixed ? [] : (row.plannedChanges ?? [])}
                                    editingId={row.editingChangeId ?? null}
                                    onEdit={fixed ? undefined : p => startEditingPlanned(index, p)}
                                    onDrop={
                                      fixed ? undefined : p => setDropTarget({ index, planned: p })
                                    }
                                    disabled={submitting}
                                    fixed={fixed}
                                  />
                                </Flex>

                                {fixed ? (
                                  <Box gridColumn={{ base: 'auto', md: '1 / -1' }}>
                                    <Text color="fg.muted">
                                      Festpreis {formatEuro(parsePriceInput(PILOT_FIXED_PRICE))}{' '}
                                      brutto, nicht änderbar.
                                    </Text>
                                  </Box>
                                ) : (
                                  <>
                                    <FieldRoot
                                      invalid={!!(error?.pricePerUnit && touched?.pricePerUnit)}
                                      gridColumn={{ base: 'auto', md: 'span 6' }}
                                    >
                                      <FieldLabel>
                                        {priceLabel} <Field.RequiredIndicator />
                                      </FieldLabel>
                                      <Input
                                        value={row.pricePerUnit}
                                        placeholder="z.B. 50,00"
                                        name={`bankServices.${index}.pricePerUnit`}
                                        inputMode="decimal"
                                        aria-label={`${label} ${priceLabel}`}
                                        disabled={submitting}
                                        onChange={e =>
                                          formik.setFieldValue(
                                            `bankServices.${index}.pricePerUnit`,
                                            e.target.value
                                          )
                                        }
                                        onBlur={formik.handleBlur}
                                      />
                                      <FieldErrorText>
                                        {touched?.pricePerUnit && (error?.pricePerUnit as any)}
                                      </FieldErrorText>
                                    </FieldRoot>

                                    {(changed && needsDate) || editing ? (
                                      <Box gridColumn={{ base: 'auto', md: 'span 6' }}>
                                        <EffectiveDatePicker
                                          name={`bankServices.${index}.validFrom`}
                                          value={row.validFrom ?? ''}
                                          onChange={v => {
                                            formik.setFieldValue(
                                              `bankServices.${index}.validFrom`,
                                              v
                                            )
                                            formik.setFieldTouched(
                                              `bankServices.${index}.validFrom`,
                                              true,
                                              false
                                            )
                                          }}
                                          required={changed && needsDate}
                                          showError={
                                            !!touched?.validFrom || formik.submitCount > 0
                                          }
                                          disabled={submitting}
                                          today={todayIso}
                                          helperText="Änderungen gelten immer ab dem 1. eines Monats."
                                        />
                                      </Box>
                                    ) : null}

                                    {editing && (
                                      <Box gridColumn={{ base: 'auto', md: '1 / -1' }}>
                                        <Button
                                          variant="ghost"
                                          onClick={() => stopEditingPlanned(index)}
                                          disabled={submitting}
                                        >
                                          Bearbeitung abbrechen
                                        </Button>
                                      </Box>
                                    )}
                                  </>
                                )}
                              </Box>
                            )
                          })}
                        </Flex>

                        <Collapsible.Root
                          open={priceHistoryOpen}
                          onOpenChange={e => setPriceHistoryOpen(e.open)}
                        >
                          <Flex direction="column" gap={2}>
                            <HStack gap={2}>
                              <Text>Verlauf</Text>
                              <Collapsible.Trigger asChild>
                                <IconButton
                                  variant="ghost"
                                  aria-label={
                                    priceHistoryOpen ? 'Verlauf ausblenden' : 'Verlauf anzeigen'
                                  }
                                  aria-expanded={priceHistoryOpen}
                                >
                                  {priceHistoryOpen ? <LuMinus /> : <LuPlus />}
                                </IconButton>
                              </Collapsible.Trigger>
                            </HStack>
                            <Collapsible.Content>
                              {priceHistoryAvailable ? (
                                <ValueChangeHistoryTable
                                  kind="price"
                                  rows={priceHistory}
                                  currentUser={currentUser}
                                />
                              ) : (
                                <Text color="fg.muted">
                                  Der Verlauf ist derzeit nicht verfügbar.
                                </Text>
                              )}
                            </Collapsible.Content>
                          </Flex>
                        </Collapsible.Root>
                      </Flex>
                    ) : (
                      <Flex direction="column" gap={8}>
                        <Box>
                          <Text>Leistungen &amp; Preise</Text>
                          <Text>
                            Bitte fügen Sie mindestens einen Service mit zugehörigem Preis hinzu.
                          </Text>
                        </Box>

                        <FieldArray
                          name="bankServices"
                          render={() => (
                            <Flex direction="column" gap={4}>
                              {(formik.values.bankServices || []).map((row, index) => {
                                const error = formik.errors.bankServices?.[index] as
                                  | FormikErrors<ServiceRow>
                                  | undefined
                                const touched = formik.touched.bankServices?.[index] as
                                  | FormikErrors<ServiceRow>
                                  | undefined

                                const unionFixed = isProtectedUnion

                                return (
                                  <Box
                                    key={index}
                                    display="grid"
                                    columnGap={4}
                                    rowGap={4}
                                    alignItems="flex-start"
                                    gridTemplateColumns={{
                                      base: '1fr',
                                      md: 'repeat(12, minmax(0, 1fr))',
                                    }}
                                  >
                                    <FieldRoot
                                      invalid={!!(error?.serviceCode && touched?.serviceCode)}
                                      gridColumn={{ base: 'auto', md: 'span 8' }}
                                    >
                                      <FieldLabel>
                                        Leistungsbezeichnung <Field.RequiredIndicator />
                                      </FieldLabel>
                                      {unionFixed ? (
                                        <Input
                                          value={row.serviceName || row.serviceCode}
                                          disabled
                                        />
                                      ) : (
                                        <>
                                          <Input
                                            list={`serviceNameOptions-${index}`}
                                            value={row.serviceName}
                                            placeholder="Eingeben oder Auswählen"
                                            onChange={e => {
                                              const name = e.target.value
                                              formik.setFieldValue(
                                                `bankServices.${index}.serviceName`,
                                                name
                                              )
                                              formik.setFieldValue(
                                                `bankServices.${index}.serviceCode`,
                                                toServiceCode(name)
                                              )
                                            }}
                                            onBlur={() => {
                                              formik.setFieldTouched(
                                                `bankServices.${index}.serviceCode`,
                                                true
                                              )
                                              ensureServiceFromName(index)
                                            }}
                                          />
                                          <datalist id={`serviceNameOptions-${index}`}>
                                            {getAvailableServiceTypes(index).map(s => (
                                              <option key={s.value} value={s.label} />
                                            ))}
                                          </datalist>
                                        </>
                                      )}

                                      <FieldErrorText>
                                        {touched?.serviceCode && (error?.serviceCode as any)}
                                      </FieldErrorText>
                                    </FieldRoot>

                                    <FieldRoot
                                      invalid={!!(error?.pricePerUnit && touched?.pricePerUnit)}
                                      gridColumn={{
                                        base: 'auto',
                                        md: isProtectedUnion ? 'span 4' : 'span 3',
                                      }}
                                    >
                                      <FieldLabel>
                                        Preis pro Einheit <Field.RequiredIndicator />
                                      </FieldLabel>
                                      <Input
                                        value={row.pricePerUnit}
                                        placeholder="z.B. 50,00"
                                        name={`bankServices.${index}.pricePerUnit`}
                                        disabled={row.serviceCode === 'PILOT'}
                                        onChange={e =>
                                          formik.setFieldValue(
                                            `bankServices.${index}.pricePerUnit`,
                                            e.target.value
                                          )
                                        }
                                        onBlur={formik.handleBlur}
                                      />
                                      {row.serviceCode === 'PILOT' && (
                                        <Text>Festpreis: 50,00 € brutto</Text>
                                      )}
                                      <FieldErrorText>
                                        {touched?.pricePerUnit && (error?.pricePerUnit as any)}
                                      </FieldErrorText>
                                    </FieldRoot>

                                    {!isProtectedUnion && (
                                      <IconButton
                                        aria-label="Leistung entfernen"
                                        variant="ghost"
                                        onClick={() => handleDeleteService(index)}
                                      >
                                        <LuTrash2 size={16} />
                                      </IconButton>
                                    )}
                                  </Box>
                                )
                              })}

                              {!isProtectedUnion && (
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    formik.setFieldValue('bankServices', [
                                      ...(formik.values.bankServices || []),
                                      { serviceCode: '', serviceName: '', pricePerUnit: '' },
                                    ])
                                  }}
                                >
                                  <LuPlus />
                                  Leistung hinzufügen
                                </Button>
                              )}

                              {!hasAtLeastOneCompleteService && (
                                <Text>
                                  Speichern ist deaktiviert bis mindestens eine Leistung inkl.
                                  Preis erfasst wurde.
                                </Text>
                              )}
                            </Flex>
                          )}
                        />
                      </Flex>
                    )}
                  </FormikProvider>
                </Box>

                <Box>
                  <HStack justify="flex-end">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        resetAll()
                        onOpenChange?.(false)
                      }}
                    >
                      Abbrechen
                    </Button>

                    {activeTab === 'MASTER' ? (
                      <Button
                        variant="solid"
                        colorPalette="blue"
                        onClick={goNext}
                        disabled={isProtectedUnion ? false : !isStep1Valid()}
                      >
                        Weiter <LuArrowRight />
                      </Button>
                    ) : (
                      <Button
                        variant="solid"
                        colorPalette="blue"
                        onClick={() => formik.handleSubmit()}
                        disabled={!canSave}
                      >
                        <LuSave /> {submitting ? 'Speichere…' : 'Änderungen Speichern'}
                      </Button>
                    )}
                  </HStack>
                </Box>
              </Flex>

              {/* Union prices with a date (SIB-941): confirmation and drop */}
              {isProtectedUnion && (
                <>
                  <ValueChangeConfirmDialog
                    isOpen={pendingPriceChanges !== null}
                    kind="price"
                    changes={pendingPriceChanges ?? []}
                    onCancel={() => setPendingPriceChanges(null)}
                    onConfirm={confirmUnionSave}
                    saving={submitting}
                  />
                  <DropPlannedChangeDialog
                    isOpen={dropTarget !== null}
                    kind="price"
                    serviceLabel={
                      dropTarget
                        ? (formik.values.bankServices[dropTarget.index]?.serviceName ?? '')
                        : ''
                    }
                    planned={dropTarget?.planned ?? null}
                    activeValue={
                      dropTarget
                        ? parsePriceInput(
                            formik.values.bankServices[dropTarget.index]?.activePrice ?? ''
                          )
                        : null
                    }
                    onCancel={() => setDropTarget(null)}
                    onConfirm={doDropPlanned}
                    dropping={dropping}
                  />
                </>
              )}

              {/* DELETE DIALOG (matches mock) */}
              {bankId != null && (
                <DeleteBankDialog
                  isOpen={deleteOpen}
                  onClose={() => setDeleteOpen(false)}
                  bankId={bankId}
                  onDeleted={() => {
                    setDeleteOpen(false)
                    onDeleted?.()
                    onOpenChange?.(false)
                  }}
                />
              )}
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  )
}
